import { useEffect, useRef, useState, useCallback } from "react"
import { CollaborationService } from "services/collaborationService"
import { collaborationManager } from "services/collaborationManager"
import { Variant } from "types/questionnaire.types"
import { WebsocketProvider } from "y-websocket"
import * as Y from "yjs"
import { getCollaborationWsUrl } from "utils/server"

interface UseCollaborationProps {
  projectId: string
  userId: string
  websocketUrl?: string
  onDocumentUpdate?: (variantName: string, content: string) => void
  onVariantChange?: (variants: Variant[]) => void
  onQuestionnaireRefresh?: () => void
}

interface VariantMetadata {
  name: string
  languages: string[]
  lastModified?: string
}

interface UseCollaborationReturn {
  collaborationService: CollaborationService | null
  isConnected: boolean
  connectionStatus: Record<string, boolean>
  switchToVariant: (variantName: string) => Y.Doc | null
  getCurrentDoc: () => Y.Doc | null
  getVariantDoc: (variantName: string) => Y.Doc | null
  getProvider: (variantName: string) => WebsocketProvider | null
  convertToTextFormat: (
    currentContent: string,
    variants: Variant[],
    currentVariant?: string,
    fullQuestionnaire?: string
  ) => string
  disconnect: () => void
  initializeVariant: (variantName: string) => Y.Doc
  updateVariantMetadata: (variantName: string, languages: string[]) => void
  removeVariantMetadata: (variantName: string) => void
  getAllVariantMetadata: () => VariantMetadata[]
}

export const useCollaboration = ({
  projectId,
  userId,
  websocketUrl = getCollaborationWsUrl(),
  onDocumentUpdate,
  onVariantChange,
  onQuestionnaireRefresh,
}: UseCollaborationProps): UseCollaborationReturn => {
  const serviceRef = useRef<CollaborationService | null>(null)
  const [isConnected, setIsConnected] = useState<boolean>(false)
  const [connectionStatus, setConnectionStatus] = useState<Record<string, boolean>>({})

  // Use refs to store callbacks and avoid re-initializing service
  const callbacksRef = useRef({
    onDocumentUpdate,
    onVariantChange,
    onQuestionnaireRefresh,
  })

  // Update callback refs when callbacks change
  useEffect(() => {
    callbacksRef.current = {
      onDocumentUpdate,
      onVariantChange,
      onQuestionnaireRefresh,
    }

    // Update existing service callbacks if service exists
    if (serviceRef.current) {
      if (onDocumentUpdate)
        serviceRef.current.setOnDocumentUpdate(onDocumentUpdate)
      if (onVariantChange)
        serviceRef.current.setOnVariantChange(onVariantChange)
      if (onQuestionnaireRefresh)
        serviceRef.current.setOnQuestionnaireRefresh(onQuestionnaireRefresh)
    }
  }, [onDocumentUpdate, onVariantChange, onQuestionnaireRefresh])

  // Initialize collaboration service only once per project
  useEffect(() => {
    if (!projectId || !userId) return

    const service = collaborationManager.getService(projectId, {
      websocketUrl,
      userId,
      onDocumentUpdate: callbacksRef.current.onDocumentUpdate,
      onVariantChange: callbacksRef.current.onVariantChange,
      onQuestionnaireRefresh: callbacksRef.current.onQuestionnaireRefresh,
    })

    serviceRef.current = service

    // Update connection status less frequently to reduce overhead
    const statusInterval = setInterval(() => {
      if (serviceRef.current) {
        const status = serviceRef.current.getConnectionStatus()
        setConnectionStatus(status)

        // Set overall connection status based on whether any variant is connected
        const anyConnected = Object.values(status).some(
          (connected) => connected
        )
        setIsConnected(anyConnected)
      }
    }, 2000)

    return () => {
      clearInterval(statusInterval)
    }
  }, [projectId, userId, websocketUrl]) // Remove callbacks from dependencies

  const switchToVariant = useCallback(
    (variantName: string): Y.Doc | null => {
      if (!serviceRef.current) return null
      return serviceRef.current.switchToVariant(variantName)
    },
    []
  )

  const getCurrentDoc = useCallback((): Y.Doc | null => {
    if (!serviceRef.current) return null
    return serviceRef.current.getCurrentDoc()
  }, [])

  const getVariantDoc = useCallback((variantName: string): Y.Doc | null => {
    if (!serviceRef.current) return null
    return serviceRef.current.getVariantDoc(variantName)
  }, [])

  const convertToTextFormat = useCallback(
    (
      currentContent: string,
      variants: Variant[],
      currentVariant?: string,
      fullQuestionnaire?: string
    ): string => {
      if (!serviceRef.current) return currentContent
      return serviceRef.current.convertToTextFormat(
        currentContent,
        variants,
        currentVariant,
        fullQuestionnaire
      )
    },
    []
  )

  const disconnect = useCallback((): void => {
    if (!serviceRef.current) return
    serviceRef.current.disconnectAll()
    setIsConnected(false)
    setConnectionStatus({})
  }, [])

  const initializeVariant = useCallback(
    (variantName: string): Y.Doc => {
      if (!serviceRef.current)
        throw new Error("Collaboration service not initialized")
      return serviceRef.current.initializeVariant(variantName)
    },
    []
  )

  const getProvider = useCallback(
    (variantName: string): WebsocketProvider | null => {
      if (!serviceRef.current) return null
      return serviceRef.current.getProvider(variantName)
    },
    []
  )

  const updateVariantMetadata = useCallback(
    (variantName: string, languages: string[]): void => {
      if (!serviceRef.current) return
      serviceRef.current.updateVariantMetadata(variantName, languages)
    },
    []
  )

  const removeVariantMetadata = useCallback((variantName: string): void => {
    if (!serviceRef.current) return
    serviceRef.current.removeVariantMetadata(variantName)
  }, [])

  const getAllVariantMetadata = useCallback((): VariantMetadata[] => {
    if (!serviceRef.current) return []
    return serviceRef.current.getAllVariantMetadata()
  }, [])

  return {
    collaborationService: serviceRef.current,
    isConnected,
    connectionStatus,
    switchToVariant,
    getCurrentDoc,
    getVariantDoc,
    getProvider,
    convertToTextFormat,
    disconnect,
    initializeVariant,
    updateVariantMetadata,
    removeVariantMetadata,
    getAllVariantMetadata,
  }
}
